import { gql, useQuery } from '@apollo/client';
import { CalendarDate } from '@internationalized/date';
import * as aq from 'arquero';
import * as React from 'react';

import { getPresetDateRange, isPresetDateRange } from '../components/DateRangePicker';

import type {
  GetTransactionsForUserQuery,
  GetTransactionsForUserQueryVariables,
} from './__generated__/TransactionsProvider.gql';
import type { CalendarDateRange } from '../components/DateRangePicker';
import type { ColumnTable } from 'arquero';

export enum TransactionType {
  Spending = 'spending',
  Income = 'income',
}

const GET_TRANSACTIONS = gql`
  query GetTransactionsForUser {
    getAllTransactionsForUser {
      id
      amount
      date
      merchant {
        id
      }
      financialAccount {
        id
        bankConnection {
          id
        }
      }
      isPaired
      isMoneyMovement
    }
  }
`;

type TransactionNode = GetTransactionsForUserQuery['getAllTransactionsForUser'][number];

export interface ITransactionRow {
  id: string;
  date: CalendarDate;
  amount: number;
  merchantId: string | null;
  financialAccountId: string | null;
  bankConnectionId: string | null;
  /** One side of a transfer between the household's accounts, bank-to-bank or into or out of an investment account. */
  isPaired: boolean;
  isMoneyMovement: boolean;
}

/** The Arquero row for one transaction: typed columns, ids flattened. */
export function toTransactionRow(t: TransactionNode): ITransactionRow {
  return {
    id: t.id,
    date: t.date,
    amount: parseFloat(t.amount),
    merchantId: t.merchant?.id ?? null,
    financialAccountId: t.financialAccount?.id ?? null,
    bankConnectionId: t.financialAccount?.bankConnection.id ?? null,
    isPaired: t.isPaired,
    isMoneyMovement: t.isMoneyMovement,
  };
}

interface ITransactionsContextValue {
  transactions: ColumnTable | null;
  loading: boolean;
  error: Error | undefined;
}

const defaultValues: ITransactionsContextValue = {
  transactions: null,
  loading: true,
  error: undefined,
};

// Exported for useFilteredTransactions hook - do not use directly elsewhere
export const TransactionsContext = React.createContext<ITransactionsContextValue>(defaultValues);

type ITransactionsProviderProps = React.PropsWithChildren;

const TransactionsProvider = ({ children }: ITransactionsProviderProps) => {
  const {
    data: transactionsData,
    loading: transactionsLoading,
    error: transactionsError,
  } = useQuery<GetTransactionsForUserQuery, GetTransactionsForUserQueryVariables>(GET_TRANSACTIONS);

  // Build Arquero table from all transactions
  const transactions = React.useMemo(() => {
    if (!transactionsData?.getAllTransactionsForUser || transactionsData.getAllTransactionsForUser.length === 0) {
      return null;
    }

    return aq.from(transactionsData.getAllTransactionsForUser.map(toTransactionRow));
  }, [transactionsData]);

  // Memoize context value to prevent unnecessary re-renders of all consumers.
  // Without this, any Apollo cache update that touches a Transaction object would
  // create a new context value object, cascading re-renders through the entire tree.
  const contextValue = React.useMemo<ITransactionsContextValue>(
    () => ({
      transactions,
      loading: transactionsLoading,
      error: transactionsError,
    }),
    [transactions, transactionsLoading, transactionsError]
  );

  return <TransactionsContext.Provider value={contextValue}>{children}</TransactionsContext.Provider>;
};

export default TransactionsProvider;

export const OLDEST_TRANSACTION_DATE_FALLBACK = new CalendarDate(2020, 1, 1);

/**
 * Returns the date of the oldest transaction in the dataset.
 * Falls back to Jan 1, 2020 if transactions haven't loaded yet.
 */
export const useOldestTransactionDate = (): CalendarDate => {
  const { transactions } = React.useContext(TransactionsContext);

  return React.useMemo(() => {
    if (!transactions || transactions.numRows() === 0) {
      return OLDEST_TRANSACTION_DATE_FALLBACK;
    }

    const dates = transactions.array('date') as CalendarDate[];
    let oldest = dates[0];
    for (let i = 1; i < dates.length; i++) {
      if (dates[i].compare(oldest) < 0) {
        oldest = dates[i];
      }
    }
    return oldest;
  }, [transactions]);
};

/**
 * Generic function to filter an Arquero table by a column value.
 *
 * @param transactions - The Arquero ColumnTable containing transactions
 * @param columnName - The name of the column to filter by
 * @param value - The value to filter for (supports null/undefined for null checks)
 * @returns Filtered ColumnTable
 */
export function filterByColumn<T>(
  transactions: ColumnTable,
  columnName: string,
  value: T | T[] | null | undefined
): ColumnTable {
  return transactions.filter(
    aq.escape((d: Record<string, T | null | undefined>) => {
      const fieldValue = d[columnName];

      // Handle null/undefined comparison
      if (value === null || value === undefined) {
        return fieldValue === null || fieldValue === undefined;
      }

      if (Array.isArray(value)) {
        return value.includes(fieldValue as T);
      } else {
        return fieldValue === value;
      }
    })
  );
}

/**
 * Filters an Arquero table by date range using CalendarDate comparison.
 *
 * @param transactions - The Arquero ColumnTable containing transactions
 * @param startDate - Start date (inclusive)
 * @param endDate - End date (inclusive)
 * @returns Filtered ColumnTable
 */
export function filterByDateRange(
  transactions: ColumnTable,
  startDate: CalendarDate,
  endDate: CalendarDate
): ColumnTable {
  return transactions.filter(
    aq.escape((d: { date: CalendarDate }) => {
      // compare returns -1 if d.date < startDate, 0 if equal, 1 if d.date > startDate
      const afterStart = d.date.compare(startDate) >= 0;
      const beforeEnd = d.date.compare(endDate) <= 0;
      return afterStart && beforeEnd;
    })
  );
}

/**
 * Filters an Arquero table to include transactions by amount type.
 *
 * In Plaid, credits are positive (amount >= 0), debits are negative (amount < 0).
 *
 * @param transactions - The Arquero ColumnTable containing transactions
 * @param type - 'credit' for credits (amount >= 0), 'debit' for debits (amount < 0)
 * @returns Filtered ColumnTable based on credit or debit type
 */
export function filterByAmountType(transactions: ColumnTable, type: 'credit' | 'debit'): ColumnTable {
  if (type === 'credit') {
    // Credits: amount >= 0
    return transactions.filter(aq.escape((d: { amount: number }) => d.amount >= 0));
  }
  // Debits: amount < 0
  return transactions.filter(aq.escape((d: { amount: number }) => d.amount < 0));
}

/**
 * Calculates the median transaction amount for a given date range.
 *
 * @param transactions - The Arquero ColumnTable containing transactions
 * @param dateRange - Date range to filter transactions
 * @returns Median transaction amount — the midpoint of the two middle amounts for an even count — or 0 if no transactions
 */
export function calculateMedianTransaction(transactions: ColumnTable | null, dateRange: CalendarDateRange): number {
  if (!transactions) {
    return 0;
  }

  const filteredTable = filterByDateRange(transactions, dateRange.start, dateRange.end);
  const medianResult = filteredTable.rollup({ median: aq.op.median('amount') }).object() as {
    median?: number;
  } | null;

  return medianResult?.median ?? 0;
}

/**
 * Calculates the total number of transactions for a given date range.
 *
 * @param transactions - The Arquero ColumnTable containing transactions
 * @param dateRange - Date range to filter transactions
 * @returns Total number of transactions, or 0 if no transactions
 */
export function calculateTotalTransactions(transactions: ColumnTable | null, dateRange: CalendarDateRange): number {
  if (!transactions) {
    return 0;
  }

  const filteredTable = filterByDateRange(transactions, dateRange.start, dateRange.end);
  return filteredTable.numRows();
}

/**
 * Calculates the total sum of transaction amounts for a given date range.
 *
 * @param transactions - The Arquero ColumnTable containing transactions
 * @param dateRange - Date range to filter transactions
 * @returns Total sum of transaction amounts, or 0 if no transactions
 */
export function calculateTotal(transactions: ColumnTable | null, dateRange: CalendarDateRange): number {
  if (!transactions) {
    return 0;
  }

  const filteredTable = filterByDateRange(transactions, dateRange.start, dateRange.end);
  const transactionCount = filteredTable.numRows();

  if (transactionCount === 0) {
    return 0;
  }

  const totalAmountResult = filteredTable.rollup({ total: aq.op.sum('amount') }).object() as {
    total: number;
  } | null;

  return totalAmountResult?.total ?? 0;
}

/**
 * Calculates the largest transaction amount for a given date range.
 * Uses intelligent logic to determine whether to show positive or negative transactions
 * based on the majority type (70% threshold).
 *
 * @param transactions - The Arquero ColumnTable containing transactions
 * @param dateRange - Date range to filter transactions
 * @returns Largest transaction amount, or 0 if no transactions
 */
export function calculateLargestTransaction(transactions: ColumnTable | null, dateRange: CalendarDateRange): number {
  if (!transactions) {
    return 0;
  }

  const filteredTable = filterByDateRange(transactions, dateRange.start, dateRange.end);
  const transactionCount = filteredTable.numRows();

  if (transactionCount === 0) {
    return 0;
  }

  // Count positive and negative transactions
  const positiveCount = filteredTable.filter(aq.escape((d: { amount: number }) => d.amount > 0)).numRows();
  const negativeCount = filteredTable.filter(aq.escape((d: { amount: number }) => d.amount < 0)).numRows();

  const positiveRatio = positiveCount / transactionCount;
  const negativeRatio = negativeCount / transactionCount;
  const threshold = 0.7; // 70% threshold

  // Determine which type to show based on majority
  let shouldShowPositive = false;
  if (positiveRatio >= threshold) {
    shouldShowPositive = true;
  } else if (negativeRatio >= threshold) {
    shouldShowPositive = false;
  } else {
    // If neither reaches threshold, default to showing the one with highest absolute value
    const minResult = filteredTable.rollup({ min: aq.op.min('amount') }).object() as {
      min: number;
    } | null;
    const maxResult = filteredTable.rollup({ max: aq.op.max('amount') }).object() as {
      max: number;
    } | null;

    const smallest = minResult?.min ?? 0;
    const largest = maxResult?.max ?? 0;
    return Math.abs(smallest) > Math.abs(largest) ? smallest : largest;
  }

  // Get the largest transaction of the determined type
  if (shouldShowPositive) {
    const positiveTransactions = filteredTable.filter(aq.escape((d: { amount: number }) => d.amount > 0));
    if (positiveTransactions.numRows() === 0) {
      return 0;
    }
    const maxResult = positiveTransactions.rollup({ max: aq.op.max('amount') }).object() as {
      max: number;
    } | null;
    return maxResult?.max ?? 0;
  } else {
    const negativeTransactions = filteredTable.filter(aq.escape((d: { amount: number }) => d.amount < 0));
    if (negativeTransactions.numRows() === 0) {
      return 0;
    }
    const minResult = negativeTransactions.rollup({ min: aq.op.min('amount') }).object() as {
      min: number;
    } | null;
    return minResult?.min ?? 0;
  }
}

/**
 * Calculates the largest withdrawal amount for a given date range.
 * In Plaid, withdrawals are positive amounts, but this function returns a negative value.
 *
 * @param transactions - The Arquero ColumnTable containing transactions
 * @param dateRange - Date range to filter transactions
 * @returns Largest withdrawal amount as a negative number, or 0 if no withdrawals
 */
export function calculateLargestWithdrawal(transactions: ColumnTable | null, dateRange: CalendarDateRange): number {
  if (!transactions) {
    return 0;
  }

  const filteredTable = filterByDateRange(transactions, dateRange.start, dateRange.end);
  const transactionCount = filteredTable.numRows();

  if (transactionCount === 0) {
    return 0;
  }

  // Filter to only positive amounts (quirk of Plaid - withdrawals are positive)
  const withdrawals = filteredTable.filter(aq.escape((d: { amount: number }) => d.amount > 0));
  const withdrawalCount = withdrawals.numRows();

  if (withdrawalCount === 0) {
    return 0;
  }

  const maxResult = withdrawals.rollup({ max: aq.op.max('amount') }).object() as {
    max: number;
  } | null;

  return (maxResult?.max ?? 0) * -1;
}

/**
 * Calculates the largest deposit amount for a given date range.
 * In Plaid, deposits are negative amounts, but this function returns a positive value.
 *
 * @param transactions - The Arquero ColumnTable containing transactions
 * @param dateRange - Date range to filter transactions
 * @returns Largest deposit amount as a positive number, or 0 if no deposits
 */
export function calculateLargestDeposit(transactions: ColumnTable | null, dateRange: CalendarDateRange): number {
  if (!transactions) {
    return 0;
  }

  const filteredTable = filterByDateRange(transactions, dateRange.start, dateRange.end);
  const transactionCount = filteredTable.numRows();

  if (transactionCount === 0) {
    return 0;
  }

  // Filter to only negative amounts (quirk of Plaid - deposits are negative)
  const deposits = filteredTable.filter(aq.escape((d: { amount: number }) => d.amount < 0));
  const depositCount = deposits.numRows();

  if (depositCount === 0) {
    return 0;
  }

  const minResult = deposits.rollup({ min: aq.op.min('amount') }).object() as {
    min: number;
  } | null;

  return (minResult?.min ?? 0) * -1;
}

export type PreviousPeriodType = 'month' | 'year' | null;

export interface IPreviousTotalResult {
  value: number;
  periodType: PreviousPeriodType;
}

/**
 * Calculates the previous period's total for a given date range.
 * Only works for 'thisMonth' and 'thisYear' presets.
 *
 * @param transactions - The Arquero ColumnTable containing transactions
 * @param dateRange - Date range to filter transactions
 * @returns Previous total result with value and periodType, or { value: 0, periodType: null } if not applicable
 */
export function calculatePreviousTotal(
  transactions: ColumnTable | null,
  dateRange: CalendarDateRange
): IPreviousTotalResult {
  if (!transactions) {
    return {
      value: 0,
      periodType: null,
    };
  }

  const isThisMonth = isPresetDateRange(dateRange, 'thisMonth');
  const isThisYear = isPresetDateRange(dateRange, 'thisYear');

  if (isThisMonth) {
    // Calculate last month's total using pastMonth preset
    const pastMonthRange = getPresetDateRange('pastMonth');
    const lastMonthTable = filterByDateRange(transactions, pastMonthRange.start, pastMonthRange.end);
    const lastMonthResult = lastMonthTable.rollup({ total: aq.op.sum('amount') }).object() as {
      total: number;
    } | null;

    return {
      value: lastMonthResult?.total ?? 0,
      periodType: 'month',
    };
  } else if (isThisYear) {
    // Calculate last year's total using pastYear preset
    const pastYearRange = getPresetDateRange('pastYear');
    const lastYearTable = filterByDateRange(transactions, pastYearRange.start, pastYearRange.end);
    const lastYearResult = lastYearTable.rollup({ total: aq.op.sum('amount') }).object() as {
      total: number;
    } | null;

    return {
      value: lastYearResult?.total ?? 0,
      periodType: 'year',
    };
  }

  return {
    value: 0,
    periodType: null,
  };
}
